<template lang="pug">
.hotspot
  DynamicMedia(:config="image" alt="Hotspot" :sizes="[626, 1284]")
  Tooltip.spot(
    :manual="true"
    v-for="(spot, i) in hotspots"
    :ref="el => tips[i] = el"
    :key="i"
    :style="{top: spot.top+'%', left: spot.left+'%'}"
  )
    .tip-wrap(@click.stop="clickTip(spot, i)")
      .show-tip
        .show-tip-inner
          Icon(:name="spot.icon || 'plus'")
    template(#tooltip)
      .hotspot-content
        StaticImage(v-if="spot.img" :width="120" :height="80" :src="spot.img.src" :alt="spot.img.alt || ''" @click.stop)
        .text
          .hotspot-title(v-if="spot.title" @click.stop) {{spot.title}}
          .hotspot-text(v-if="spot.text" v-html="spot.text" @click.stop)
          RogueLink.hotspot-link(v-if="spot.link" @click="hideTips()" :to="spot.link" :ref="el => links[i] = el")
            | {{spot.linkText || 'Learn More'}}
            Icon(name="arrow-right")
</template>
<script setup lang="ts">
import { ref, type ComponentPublicInstance } from 'vue'
import { useMQ } from '../../../js/mq'
import type Tooltip from '../../core/async/Tooltip.vue'

type ImageConfig = Record<string, unknown>
type Hotspot = {
  top: number
  left: number
  title?: string
  icon?: string
  img?: { src: string }
  text?: string
  link?: string
  linkText?: string
}

withDefaults(defineProps<{
  image: ImageConfig,
  hotspots?: Hotspot[],
}>(), {
  hotspots: () => [],
})

const tips = ref<(InstanceType<typeof Tooltip> | null)[]>([])
const links = ref<(ComponentPublicInstance | null)[]>([])
const mq = useMQ()

function clickTip(spot: Hotspot, i: number) {
  const link = links.value?.[i]
  if (link && mq.value === 'mobile') {
    link?.$el?.click()
  } else {
    tips.value?.forEach((tip, j) => {
      if (j !== i) tip?.destroy?.()
    })
    const tip = tips.value?.[i]
    if (tip && typeof tip.toggle === 'function') {
      tip.toggle()
    }
  }
}

function hideTips() {
  tips.value?.forEach((tip) => tip?.destroy?.())
}
</script>
<style lang="scss" scoped>

.hotspot {
  position: relative;

  :deep(.tip) {
    max-width: 500px;
  }
}

.spot {
  position: absolute;
}

.hotspot-content {
  width: 480px;
  max-width: 75vw;
  display: flex;

  .rg-img {
    flex-shrink: 0;
    width: 120px;
    height: 80px;
  }
}

.text {
  margin-left: $space-xs;
}

.show-tip {
  background-color: white;
  border-radius: $radius-circle;
  border: 2px solid white;
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.11);
  cursor: pointer;

  &:hover {
    background-color: $color-gray-light;
  }
}

.show-tip-inner {
  border-radius: $radius-circle;
  padding: $space-xxs;
  border: $border-width-medium solid $color-red;

  .icon {
    width: 16px;
    height: 16px;
  }
}
.tip-wrap {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@include mobile() {
  .show-tip-inner {
    padding: $space-xxxs;
    border-width: $border-width-thin;

    .icon {
      width: 8px;
      height: 8px;
    }
  }

}

.hotspot-title {
  font-size: $size-s;
  font-weight: $weight-bold;
  margin-bottom: $space-s;
  text-transform: uppercase;
}

.hotspot-text {
  margin-bottom: $space-s;
}

.hotspot-link {
  display: flex;
  font-size: $size-s;
  text-decoration: none;
  color: $color-black-light;
  align-items: center;

  .icon {
    margin-left: $space-xxxs;
    width: 12px;
  }
}

</style>
